import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { FileText, Save, Plus, Trash2, Info } from "lucide-react";
import { toast } from "sonner";

export default function Terms() {
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    api.get("/terms").then(r => {
      setTerms(r.data.terms || []);
      setUpdatedAt(r.data.updated_at);
    }).finally(() => setLoading(false));
  }, []);

  const updateTerm = (i, v) => {
    const next = [...terms];
    next[i] = v;
    setTerms(next);
  };
  const removeTerm = (i) => setTerms(terms.filter((_, ix) => ix !== i));
  const addTerm = () => setTerms([...terms, "**New Clause:** Enter text..."]);

  const save = async () => {
    if (terms.some(t => !t.trim())) { toast.error("Terms cannot be empty"); return; }
    setSaving(true);
    try {
      await api.put("/terms", { terms });
      toast.success("Terms saved. Applied to new warranty certificates.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#64748B]">Configuration</div>
          <h1 className="text-3xl tracking-tighter font-medium mt-1">Terms &amp; Conditions</h1>
          <p className="text-[#475569] text-sm mt-1 max-w-2xl">
            These clauses print on Page 3 of every warranty certificate PDF. Changes apply immediately to newly downloaded PDFs.
          </p>
        </div>
        <button data-testid="terms-save-btn" onClick={save} disabled={saving}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#E63946] hover:bg-[#D32F2F] rounded-md text-sm font-medium disabled:opacity-60">
          <Save size={14} /> {saving ? "Saving..." : "Save Terms"}
        </button>
      </div>

      <div className="rounded-md border border-[#F59E0B]/30 bg-[#F59E0B]/10 text-[#F59E0B] text-xs px-3 py-2 inline-flex items-start gap-2 max-w-3xl">
        <Info size={14} className="mt-0.5 flex-shrink-0" />
        <span>
          Use <span className="font-mono kbd">**Bold Label:**</span> at the start of each clause for the printed heading. Existing warranty PDFs re-render with the latest terms each time they're downloaded.
        </span>
      </div>

      {updatedAt && <div className="text-xs text-[#64748B]">Last updated: {new Date(updatedAt).toLocaleString("en-IN")}</div>}

      {loading ? <div className="text-sm text-[#475569]">Loading…</div> : (
        <div className="space-y-3">
          {terms.map((t, i) => (
            <div key={i} className="rounded-xl border border-[#E5E7EB] bg-[#FFFFFF] p-4 flex gap-3" data-testid={`term-row-${i}`}>
              <div className="w-8 pt-3 text-[#E63946] font-mono text-sm">{i + 1}.</div>
              <textarea
                value={t}
                onChange={(e) => updateTerm(i, e.target.value)}
                rows={2}
                data-testid={`term-input-${i}`}
                className="flex-1 px-3 py-2 bg-[#FFFFFF] border border-[#E5E7EB] rounded-md text-slate-900 text-sm focus:outline-none focus:border-[#E63946] resize-y"
              />
              <button data-testid={`term-remove-${i}`} onClick={() => removeTerm(i)}
                className="self-start p-2 text-[#EF4444] hover:bg-[#EF4444]/10 rounded-md">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          <button data-testid="term-add-btn" onClick={addTerm}
            className="w-full py-3 border border-dashed border-[#E5E7EB] rounded-xl text-[#475569] hover:text-slate-900 hover:bg-[#FFFFFF] text-sm inline-flex items-center justify-center gap-2">
            <Plus size={14} /> Add clause
          </button>
        </div>
      )}
    </div>
  );
}
